'use client';
import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import { SUBJECTS, SUBJECT_CATEGORIES, DEVELOPMENTAL_LEVELS, type SubjectCategory, type DevelopmentalLevel } from '@/lib/curriculum-data';
import Link from 'next/link';

const LEVEL_ORDER: DevelopmentalLevel[] = ['infant', 'toddler', 'preschool', 'kindergarten', 'early_childhood', 'foundation', 'elementary', 'middle_school', 'high_school', 'college_prep', 'undergraduate', 'graduate', 'continuing_ed'];

const SUBJECT_MODULES: Record<string, { modules: { title: string; lessons: { title: string; duration: string; type: string; completed?: boolean }[] }[] }> = {
  mathematics: {
    modules: [
      {
        title: 'Module 1: Number Sense & Operations',
        lessons: [
          { title: 'Counting & Number Recognition', duration: '20 min', type: 'Video + Game', completed: true },
          { title: 'Addition & Subtraction Basics', duration: '30 min', type: 'Interactive', completed: true },
          { title: 'Multiplication Tables 1–12', duration: '35 min', type: 'Video + Quiz', completed: false },
          { title: 'Division Fundamentals', duration: '35 min', type: 'Worksheet', completed: false },
          { title: 'Fractions & Decimals', duration: '40 min', type: 'Video + Lab', completed: false },
        ],
      },
      {
        title: 'Module 2: Algebra',
        lessons: [
          { title: 'Variables & Expressions', duration: '35 min', type: 'Video + Practice', completed: false },
          { title: 'Linear Equations', duration: '40 min', type: 'Interactive', completed: false },
          { title: 'Quadratic Equations', duration: '45 min', type: 'Video + Quiz', completed: false },
          { title: 'Systems of Equations', duration: '45 min', type: 'Worksheet', completed: false },
        ],
      },
      {
        title: 'Module 3: Geometry',
        lessons: [
          { title: 'Shapes & Properties', duration: '30 min', type: 'Video + Game', completed: false },
          { title: 'Area & Perimeter', duration: '35 min', type: 'Interactive Lab', completed: false },
          { title: 'Pythagoras Theorem', duration: '40 min', type: 'Video + Quiz', completed: false },
          { title: 'Trigonometry Basics', duration: '45 min', type: 'Worksheet', completed: false },
        ],
      },
    ],
  },
  quran: {
    modules: [
      {
        title: 'Module 1: Juz Amma (Juz 30)',
        lessons: [
          { title: 'Surah Al-Fatiha — Memorization & Meaning', duration: '30 min', type: 'Audio + Repeat', completed: true },
          { title: 'Surah Al-Nas — Memorization', duration: '20 min', type: 'Audio + Repeat', completed: true },
          { title: 'Surah Al-Falaq — Memorization', duration: '20 min', type: 'Audio + Repeat', completed: false },
          { title: 'Surah Al-Ikhlas — Memorization & Tafsir', duration: '25 min', type: 'Audio + Video', completed: false },
          { title: 'Surah Al-Masad to Al-Kawthar', duration: '35 min', type: 'Audio + Quiz', completed: false },
        ],
      },
      {
        title: 'Module 2: Tajweed Foundations',
        lessons: [
          { title: 'Makhaarij al-Huroof (Articulation Points)', duration: '40 min', type: 'Video + Practice', completed: false },
          { title: 'Noon Saakinah Rules', duration: '35 min', type: 'Video + Quiz', completed: false },
          { title: 'Meem Saakinah Rules', duration: '30 min', type: 'Video + Practice', completed: false },
          { title: 'Madd (Elongation) Rules', duration: '40 min', type: 'Video + Quiz', completed: false },
        ],
      },
    ],
  },
  biology: {
    modules: [
      {
        title: 'Module 1: Cell Biology',
        lessons: [
          { title: 'Introduction to Cells', duration: '30 min', type: 'Video + Animation', completed: false },
          { title: 'Cell Structure & Organelles', duration: '45 min', type: 'Interactive Lab', completed: false },
          { title: 'Plant vs Animal Cells', duration: '35 min', type: 'Video + Quiz', completed: false },
          { title: 'Cell Division: Mitosis', duration: '45 min', type: 'Animation + Quiz', completed: false },
          { title: 'Cell Division: Meiosis', duration: '45 min', type: 'Animation + Quiz', completed: false },
        ],
      },
      {
        title: 'Module 2: Genetics',
        lessons: [
          { title: 'DNA Structure & Function', duration: '40 min', type: 'Video + Lab', completed: false },
          { title: 'Mendelian Genetics', duration: '45 min', type: 'Interactive', completed: false },
          { title: 'Genetic Inheritance Patterns', duration: '45 min', type: 'Video + Quiz', completed: false },
        ],
      },
    ],
  },
};

export default function SubjectHubPage() {
  const [selectedCategory, setSelectedCategory] = useState<SubjectCategory | 'all'>('all');
  const [selectedLevel, setSelectedLevel] = useState<DevelopmentalLevel | 'all'>('all');
  const [selectedSubject, setSelectedSubject] = useState<string | null>(null);
  const [expandedModule, setExpandedModule] = useState<number | null>(0);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredSubjects = SUBJECTS.filter(s => {
    const matchesCategory = selectedCategory === 'all' || s.category === selectedCategory;
    const matchesLevel = selectedLevel === 'all' || s.levels.includes(selectedLevel as DevelopmentalLevel);
    const matchesSearch = !searchQuery || s.name.toLowerCase().includes(searchQuery.toLowerCase()) || s.nameAr?.includes(searchQuery);
    return matchesCategory && matchesLevel && matchesSearch;
  });

  const subject = selectedSubject ? SUBJECTS.find(s => s.id === selectedSubject) : null;
  const modules = selectedSubject ? SUBJECT_MODULES[selectedSubject] : null;

  return (
    <AppLayout activeRoute="/subject-hub">
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-800 text-foreground">Subject Hub</h1>
            <p className="text-sm text-muted-foreground mt-1">{SUBJECTS.length} subjects · Baby to PhD · All curriculum standards</p>
          </div>
          {selectedSubject && (
            <button onClick={() => setSelectedSubject(null)} className="text-sm text-primary font-600 hover:underline">
              ← All Subjects
            </button>
          )}
        </div>

        {!selectedSubject ? (
          <>
            {/* Search & Filters */}
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                placeholder="Search subjects in English or Arabic..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="flex-1 bg-card border border-border rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
              <select
                value={selectedLevel}
                onChange={e => setSelectedLevel(e.target.value as any)}
                className="bg-card border border-border rounded-xl px-3 py-2.5 text-sm text-foreground focus:outline-none"
              >
                <option value="all">All Levels</option>
                {LEVEL_ORDER.map(level => (
                  <option key={level} value={level}>{DEVELOPMENTAL_LEVELS[level].emoji} {DEVELOPMENTAL_LEVELS[level].label}</option>
                ))}
              </select>
            </div>

            {/* Category Filters */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-600 border transition-all ${selectedCategory === 'all' ? 'bg-primary text-white border-primary' : 'bg-card border-border text-foreground hover:border-primary/40'}`}
              >
                📚 All ({SUBJECTS.length})
              </button>
              {(Object.entries(SUBJECT_CATEGORIES) as [SubjectCategory, any][]).map(([key, cat]) => {
                const count = SUBJECTS.filter(s => s.category === key).length;
                return (
                  <button
                    key={key}
                    onClick={() => setSelectedCategory(key)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-600 border transition-all ${selectedCategory === key ? 'bg-primary text-white border-primary' : 'bg-card border-border text-foreground hover:border-primary/40'}`}
                  >
                    {cat.emoji} {cat.label} ({count})
                  </button>
                );
              })}
            </div>

            {/* Subject Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {filteredSubjects.map(subject => (
                <button
                  key={subject.id}
                  onClick={() => setSelectedSubject(subject.id)}
                  className="bg-card rounded-2xl border border-border p-4 hover:shadow-md hover:border-primary/40 transition-all group text-left"
                >
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${subject.gradient} flex items-center justify-center text-2xl mb-3 shadow-sm group-hover:scale-110 transition-transform`}>
                    {subject.emoji}
                  </div>
                  <p className="text-sm font-700 text-foreground">{subject.name}</p>
                  {subject.nameAr && <p className="text-xs text-muted-foreground mt-0.5">{subject.nameAr}</p>}
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-[10px] text-muted-foreground">{subject.totalLessons} lessons</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-600 ${SUBJECT_CATEGORIES[subject.category]?.color}`}>
                      {SUBJECT_CATEGORIES[subject.category]?.emoji}
                    </span>
                  </div>
                  <div className="mt-2">
                    <div className="flex flex-wrap gap-0.5">
                      {subject.levels.slice(0, 4).map(level => (
                        <span key={level} className="text-[9px] bg-muted text-muted-foreground rounded px-1 py-0.5">
                          {DEVELOPMENTAL_LEVELS[level].emoji}
                        </span>
                      ))}
                      {subject.levels.length > 4 && (
                        <span className="text-[9px] text-muted-foreground">+{subject.levels.length - 4}</span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {filteredSubjects.length === 0 && (
              <div className="text-center py-16">
                <p className="text-4xl mb-3">🔍</p>
                <p className="font-700 text-foreground">No subjects found</p>
                <p className="text-sm text-muted-foreground mt-1">Try adjusting your filters or search query</p>
              </div>
            )}
          </>
        ) : subject ? (
          /* Subject Detail View */
          <div className="space-y-6">
            {/* Subject Header */}
            <div className={`bg-gradient-to-br ${subject.gradient} rounded-2xl p-6 text-white`}>
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center text-4xl">
                  {subject.emoji}
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-800">{subject.name}</h2>
                  {subject.nameAr && <p className="text-lg opacity-90 mt-0.5">{subject.nameAr}</p>}
                  <p className="text-sm opacity-80 mt-2">{subject.description}</p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <span className="text-xs bg-white/20 rounded-xl px-3 py-1 font-600">{subject.totalLessons} lessons</span>
                    <span className="text-xs bg-white/20 rounded-xl px-3 py-1 font-600">{subject.levels.length} levels</span>
                    <span className="text-xs bg-white/20 rounded-xl px-3 py-1 font-600">{subject.gameCount} games</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Level Availability */}
            <div className="bg-card rounded-2xl border border-border p-5">
              <h3 className="font-700 text-foreground mb-3">Available Levels</h3>
              <div className="flex flex-wrap gap-2">
                {LEVEL_ORDER.map(level => {
                  const available = subject.levels.includes(level);
                  const info = DEVELOPMENTAL_LEVELS[level];
                  return (
                    <span
                      key={level}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-600 border ${available ? info.color : 'bg-muted text-muted-foreground/40 border-border'}`}
                    >
                      {info.emoji} {info.label}
                      {!available && <span className="text-[10px]">—</span>}
                    </span>
                  );
                })}
              </div>
            </div>

            {/* Modules & Lessons */}
            <div>
              <h3 className="font-800 text-foreground mb-4">Curriculum Modules</h3>
              {modules ? (
                <div className="space-y-3">
                  {modules.modules.map((mod, modIdx) => {
                    const completedCount = mod.lessons.filter(l => l.completed).length;
                    const isExpanded = expandedModule === modIdx;
                    return (
                      <div key={modIdx} className="bg-card rounded-2xl border border-border overflow-hidden">
                        <button
                          onClick={() => setExpandedModule(isExpanded ? null : modIdx)}
                          className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center text-sm font-800 text-primary">
                              {modIdx + 1}
                            </div>
                            <div className="text-left">
                              <p className="font-700 text-sm text-foreground">{mod.title}</p>
                              <p className="text-xs text-muted-foreground">{mod.lessons.length} lessons · {completedCount} completed</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="w-24 h-1.5 bg-muted rounded-full overflow-hidden">
                              <div className="h-full bg-primary rounded-full" style={{ width: `${(completedCount / mod.lessons.length) * 100}%` }} />
                            </div>
                            <span className="text-muted-foreground text-sm">{isExpanded ? '▲' : '▼'}</span>
                          </div>
                        </button>
                        {isExpanded && (
                          <div className="border-t border-border">
                            {mod.lessons.map((lesson, lessonIdx) => (
                              <Link
                                key={lessonIdx}
                                href="/lesson-player"
                                className="flex items-center gap-4 px-4 py-3 hover:bg-muted/50 transition-colors border-b border-border last:border-0"
                              >
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs flex-shrink-0 ${lesson.completed ? 'bg-emerald-100 text-emerald-700' : 'bg-muted text-muted-foreground'}`}>
                                  {lesson.completed ? '✓' : lessonIdx + 1}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className={`text-sm font-600 ${lesson.completed ? 'text-muted-foreground line-through' : 'text-foreground'}`}>{lesson.title}</p>
                                  <p className="text-xs text-muted-foreground">{lesson.duration} · {lesson.type}</p>
                                </div>
                                <span className={`text-xs px-2 py-0.5 rounded-lg font-600 flex-shrink-0 ${lesson.completed ? 'bg-emerald-100 text-emerald-700' : 'bg-primary/10 text-primary'}`}>
                                  {lesson.completed ? 'Done' : 'Start'}
                                </span>
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {/* Placeholder modules for subjects without detailed data */}
                  {modules.modules.length < 3 && (
                    <div className="bg-muted/50 rounded-2xl border border-dashed border-border p-5 text-center">
                      <p className="text-sm text-muted-foreground">More modules coming soon · {subject.totalLessons - modules.modules.reduce((acc, m) => acc + m.lessons.length, 0)} additional lessons being added</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="bg-card rounded-2xl border border-border p-4">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center text-sm font-800 text-primary">{i}</div>
                        <div>
                          <p className="font-700 text-sm text-foreground">Module {i}: {['Foundations', 'Intermediate Concepts', 'Advanced Applications'][i - 1]}</p>
                          <p className="text-xs text-muted-foreground">{[8, 10, 12][i - 1]} lessons</p>
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        {Array.from({ length: 4 }).map((_, j) => (
                          <div key={j} className="flex items-center gap-3 px-3 py-2 bg-muted rounded-xl">
                            <span className="w-5 h-5 rounded-full bg-card border border-border flex items-center justify-center text-[10px] text-muted-foreground">{j + 1}</span>
                            <span className="text-xs text-foreground">Lesson {j + 1} — {subject.name} Core Concept</span>
                            <span className="ml-auto text-xs text-primary font-600">Start →</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </AppLayout>
  );
}
